import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { SubcontractorBid } from '@models/subcontractor.model';
import { useTranslation } from '@hooks/useTranslation';
import { Badge } from '@components/atoms/Badge';
import { formatDateOnly } from '@utils/formatDate';
import { formatMoney, t } from '../utils/format';

interface BidsTabProps {
  bids: SubcontractorBid[];
  subOrganizationName: string;
}

const OUTCOME_VARIANT: Record<SubcontractorBid['outcome'], 'success' | 'error' | 'warning' | 'secondary' | 'default'> = {
  ACCEPTED: 'success',
  DECLINED: 'error',
  COUNTERED: 'warning',
  OPEN: 'default',
  CANCELLED: 'secondary',
};

export const BidsTab: React.FC<BidsTabProps> = ({ bids, subOrganizationName }) => {
  const tSub = useTranslation('subcontractors');
  const history = useHistory();

  const outcomeLabel: Record<SubcontractorBid['outcome'], string> = {
    ACCEPTED: tSub?.outcomeAccepted || 'Accepted',
    DECLINED: tSub?.outcomeDeclined || 'Declined',
    COUNTERED: tSub?.outcomeCountered || 'Countered',
    OPEN: tSub?.outcomeOpen || 'Open',
    CANCELLED: tSub?.outcomeCancelled || 'Cancelled',
  };

  const summary = useMemo(() => {
    const closed = bids.filter((b) => ['ACCEPTED', 'DECLINED', 'CANCELLED'].includes(b.outcome));
    const accepted = bids.filter((b) => b.outcome === 'ACCEPTED');
    const winRate = closed.length > 0 ? Math.round((accepted.length / closed.length) * 100) : null;
    const withCeiling = accepted.filter((b) => b.maxBudgetAmount && b.agreedAmount != null);
    const avgVsCeiling =
      withCeiling.length > 0
        ? Math.round(
            (withCeiling.reduce((sum, b) => sum + (b.agreedAmount as number) / (b.maxBudgetAmount as number), 0) /
              withCeiling.length) *
              100,
          )
        : null;
    const openCount = bids.filter((b) => b.outcome === 'OPEN').length;
    return { closed, accepted, winRate, avgVsCeiling, openCount };
  }, [bids]);

  if (bids.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-6">
        {t(tSub?.emptyBids, 'No bids exchanged with {sub} yet.', { sub: subOrganizationName })}
      </p>
    );
  }

  return (
    <div className="pb-12">
      <div className="flex flex-wrap gap-2 mb-4">
        {summary.winRate != null && (
          <Badge variant="secondary">
            {t(tSub?.winRate, 'Win rate {pct}% ({won} of {closed} closed)', {
              pct: summary.winRate,
              won: summary.accepted.length,
              closed: summary.closed.length,
            })}
          </Badge>
        )}
        {summary.avgVsCeiling != null && (
          <Badge variant="secondary">
            {t(tSub?.avgVsCeiling, 'Accepted avg {pct}% of ceiling', { pct: summary.avgVsCeiling })}
          </Badge>
        )}
        {summary.openCount > 0 && (
          <Badge variant="default">{t(tSub?.openBids, '{n} open', { n: summary.openCount })}</Badge>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg ring-1 ring-gray-900/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="px-4 py-2">{tSub?.colSent || 'Sent'}</th>
              <th className="px-4 py-2">{tSub?.colProjectTask || 'Project · Task'}</th>
              <th className="px-4 py-2 text-right">{tSub?.colCeiling || 'My ceiling'}</th>
              <th className="px-4 py-2 text-right">{tSub?.colTheirBid || 'Their bid'}</th>
              <th className="px-4 py-2">{tSub?.colOutcome || 'Outcome'}</th>
              <th className="px-4 py-2 text-right">{tSub?.colAgreed || 'Agreed'}</th>
            </tr>
          </thead>
          <tbody>
            {bids.map((bid) => (
              <tr
                key={bid.budgetRequestId}
                onClick={() => history.push('/budgets')}
                className="border-t border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{formatDateOnly(bid.sentAt)}</td>
                <td className="px-4 py-2 text-gray-800 dark:text-gray-100">
                  {bid.projectName ? `${bid.projectName} · ` : ''}
                  {bid.taskName}
                </td>
                <td className="px-4 py-2 text-right">{formatMoney(bid.maxBudgetAmount, '—')}</td>
                <td className="px-4 py-2 text-right">{formatMoney(bid.proposedAmount, '—')}</td>
                <td className="px-4 py-2">
                  <Badge size="sm" variant={OUTCOME_VARIANT[bid.outcome]}>
                    {outcomeLabel[bid.outcome]}
                  </Badge>
                </td>
                <td className="px-4 py-2 text-right">{formatMoney(bid.agreedAmount, '—')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BidsTab;
